import { apiClient } from '../api/apiClient';
import { dashboardService, AppointmentWithDetails } from '../dashboard/dashboardService';

export interface Patient {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  totalAppointments: number;
  lastAppointmentDate?: string;
  firstAppointmentDate?: string;
  emailVerified?: boolean;
  isActive?: boolean;
  /** From API: users.onboarding_status (e.g. 'completed' | 'incomplete') */
  onboardingStatus?: string | null;
  /** Display value derived from onboarding_status: 'completed' | 'incomplete' */
  verificationStatus?: string | null;
  /** Set when patient was created by a doctor (for delete-unverified option) */
  createdByDoctorId?: number | null;
}

// Extended appointment type to include patient_id
interface AppointmentWithPatientId extends AppointmentWithDetails {
  patient_id?: number;
}

export interface PatientWithAppointments extends Patient {
  isActive?: boolean;
  appointments: AppointmentWithDetails[];
}

export interface MealPlanPreferences {
  primaryGoal?: string;
  secondaryGoals?: string[];
  dietaryPattern?: string;
  cuisinePreferences?: string[];
  dislikedFoods?: string[];
  allergies?: string[];
  medicalConditions?: string[];
  mealsPerDay?: number;
  cookingTimePerMealMin?: number;
  budgetLevel?: string;
  activityLevel?: string;
  additionalNotes?: string;
}

export interface MealPlanMeals {
  breakfast?: any;
  lunch?: any;
  dinner?: any;
  snacks?: any;
  totalCaloriesKcal?: number | null;
  nutritionProfileId?: string | null;
  // Base ICMR food group recommendations (before adjustments)
  baseIcmrRecommendations?: {
    cereals_millets_g?: number;
    pulses_beans_g?: number;
    glv_g?: number;
    vegetables_g?: number;
    roots_tubers_g?: number;
    fruits_g?: number;
    nuts_g?: number;
    milk_curd_ml?: number;
    fats_oils_g?: number;
    meat_poultry_g?: number;
    fish_seafood_g?: number;
    egg_g?: number;
    source_demographics?: {
      gender?: string;
      age?: number;
      weight_kg?: number;
      activity_level?: string;
    };
  } | null;
  // Adjusted food group recommendations (after goal-specific adjustments from agent)
  foodGroupRecommendations?: {
    cereals_millets_g?: number;
    pulses_beans_g?: number;
    glv_g?: number;
    vegetables_g?: number;
    roots_tubers_g?: number;
    fruits_g?: number;
    nuts_g?: number;
    milk_curd_ml?: number;
    fats_oils_g?: number;
    meat_poultry_g?: number;
    fish_seafood_g?: number;
    egg_g?: number;
    source_demographics?: {
      gender?: string;
      age?: number;
      weight_kg?: number;
      activity_level?: string;
    };
  } | null;
  // Doctor-modified food group recommendations (after doctor review via chat)
  doctorModifiedRecommendations?: {
    cereals_millets_g?: number;
    pulses_beans_g?: number;
    glv_g?: number;
    vegetables_g?: number;
    roots_tubers_g?: number;
    fruits_g?: number;
    nuts_g?: number;
    milk_curd_ml?: number;
    fats_oils_g?: number;
    meat_poultry_g?: number;
    fish_seafood_g?: number;
    egg_g?: number;
    source_demographics?: {
      gender?: string;
      age?: number;
      weight_kg?: number;
      activity_level?: string;
    };
  } | null;
  // Nutrient values
  baseIcmrNutrients?: Record<string, number | null> | null;
  adjustedNutrients?: Record<string, number | null> | null;
  doctorModifiedNutrients?: Record<string, number | null> | null;
}

/** Plan status for portal: approved = has meal plan; in_progress = draft/dangling */
export type MealPlanStatus = 'approved' | 'in_progress';

export interface PatientMealPlan {
  goalId: number;
  goalName: string;
  effectiveAt?: string | null;
  expiresAt?: string | null;
  isActive?: boolean;
  /** Doctor who created this plan (portal: allow delete by creator) */
  createdByDoctorId?: number | null;
  /** Chat session for requirements workflow (portal: link to conversation) */
  requirementsChatSessionId?: number | null;
  /** approved = has meal plan; in_progress = draft/dangling */
  planStatus?: MealPlanStatus | null;
  // Agent-collected configuration context used for this plan
  targetsContext?: {
    goal_specific_info?: Record<string, any>;
    required_inputs?: { field_key?: string; question?: string; answer?: string }[];
    required_adjustments?: Record<string, any>;
    targets?: { field_key?: string; display_name?: string; value?: any; question?: string }[];
    derived_adjustments?: {
      label?: string;
      nutrient_key?: string;
      old_value?: number;
      new_value?: number;
      change_percent?: number | null;
      change_absolute?: number;
    }[];
  } | null;
  mealPlan: MealPlanMeals | null;
  targets?: any[];
}

class PatientsService {
  /**
   * Get all unique patients from doctor's appointments
   */
  async getPatients(): Promise<Patient[]> {
    try {
      const response = await apiClient.get('/api/v1/doctors/patients');
      const patients: Patient[] = (response.data || []).map((p: any) => ({
        id: p.id,
        name: p.name || p.email || 'Unknown',
        email: p.email || undefined,
        totalAppointments: p.total_appointments ?? 0,
        lastAppointmentDate: p.last_appointment_date || undefined,
        firstAppointmentDate: undefined, // not provided by API; keep for compatibility
        emailVerified: p.email_verified ?? false,
        isActive: p.is_active ?? false,
        onboardingStatus: p.onboarding_status ?? null,
        verificationStatus: p.onboarding_status === 'completed' ? 'completed' : 'incomplete',
        createdByDoctorId: p.created_by_doctor_id ?? null,
      }));
      // Sort by last appointment date (most recent first)
      return patients.sort((a, b) => {
        if (!a.lastAppointmentDate && !b.lastAppointmentDate) return 0;
        if (!a.lastAppointmentDate) return 1;
        if (!b.lastAppointmentDate) return -1;
        return new Date(b.lastAppointmentDate).getTime() - new Date(a.lastAppointmentDate).getTime();
      });
    } catch (error) {
      console.error('Failed to get patients:', error);
      throw error;
    }
  }

  /**
   * Get patient details with all appointments
   */
  async getPatientDetails(patientId: number): Promise<PatientWithAppointments | null> {
    try {
      // Get summary info for all patients and locate this patient
      const patients = await this.getPatients();
      const summary = patients.find((p) => p.id === patientId);

      if (!summary) {
        return null;
      }

      // Load all appointments and filter those belonging to this patient
      const appointments = await dashboardService.getAppointments(undefined, 1000, 0);

      const patientAppointments = (appointments as AppointmentWithPatientId[]).filter((apt) => {
        const matchesId = apt.patient_id === patientId;
        const matchesEmail = summary.email && apt.patient_email === summary.email;
        const matchesName = apt.patient_name === summary.name;
        return matchesId || matchesEmail || matchesName;
      });

      const sortedAppointments = [...patientAppointments].sort(
        (a, b) => new Date(b.appointment_date).getTime() - new Date(a.appointment_date).getTime()
      );

      return {
        ...summary,
        totalAppointments: patientAppointments.length,
        lastAppointmentDate:
          sortedAppointments[0]?.appointment_date || summary.lastAppointmentDate,
        firstAppointmentDate:
          sortedAppointments[sortedAppointments.length - 1]?.appointment_date ||
          summary.firstAppointmentDate,
        appointments: sortedAppointments,
      };
    } catch (error) {
      console.error('Failed to get patient details:', error);
      throw error;
    }
  }

  /**
   * Delete a patient created by the current doctor, only if the patient has not verified their email.
   * Backend returns 400 if already verified, 403 if not the creating doctor.
   */
  async deletePatient(patientId: number): Promise<{ message: string }> {
    const { data } = await apiClient.delete<{ message: string }>(
      `/api/v1/doctors/patients/${patientId}`
    );
    return data;
  }

  /**
   * Get all meal plans (goals) for a patient
   */
  async getAllPatientMealPlans(patientId: number): Promise<PatientMealPlan[]> {
    try {
      const response = await apiClient.get(
        `/api/v1/doctors/patients/${patientId}/meal-plans`,
      );
      let data = response.data;
      
      // Log response for debugging
      console.log('getAllPatientMealPlans response:', { 
        status: response.status, 
        dataType: typeof data, 
        isArray: Array.isArray(data),
        dataLength: Array.isArray(data) ? data.length : 'N/A',
        data: data 
      });
      
      if (!data) {
        console.warn('getAllPatientMealPlans: response.data is null or undefined');
        return [];
      }
      
      if (!Array.isArray(data)) {
        console.error('getAllPatientMealPlans: response.data is not an array:', data);
        // If it's a single object (backwards compatibility), wrap it in an array
        if (data && typeof data === 'object' && data.goal) {
          console.warn('getAllPatientMealPlans: received single object, wrapping in array');
          data = [data];
        } else {
          return [];
        }
      }

      return data.map((item: any) => {
        if (!item || !item.goal) {
          console.warn('getAllPatientMealPlans: item missing goal:', item);
          return null;
        }
        
        const goal = item.goal;
        const mealPlan = item.meal_plan || null;
        const targets = Array.isArray(item?.targets) ? item.targets : [];

        return {
          goalId: goal.id,
          goalName: goal.goal_name,
          effectiveAt: goal.effective_at ?? null,
          expiresAt: goal.expires_at ?? null,
          isActive: goal.status === 'active',
          createdByDoctorId: goal.created_by_doctor_id ?? null,
          requirementsChatSessionId: goal.requirements_chat_session_id ?? null,
          planStatus: (item.plan_status === 'approved' || item.plan_status === 'in_progress' ? item.plan_status : null) ?? null,
          targetsContext: mealPlan?.targets_context ?? null,
          mealPlan: mealPlan
            ? {
                breakfast: mealPlan.breakfast,
                lunch: mealPlan.lunch,
                dinner: mealPlan.dinner,
                snacks: mealPlan.snacks,
                totalCaloriesKcal:
                  mealPlan.total_calories_kcal ?? mealPlan.totalCaloriesKcal ?? null,
                nutritionProfileId: mealPlan.nutrition_profile_id ?? null,
                baseIcmrRecommendations: mealPlan.base_icmr_recommendations ?? null,
                foodGroupRecommendations: mealPlan.food_group_recommendations ?? null,
                doctorModifiedRecommendations: mealPlan.doctor_modified_recommendations ?? null,
                baseIcmrNutrients: mealPlan.base_icmr_nutrients ?? null,
                adjustedNutrients: mealPlan.adjusted_nutrients ?? null,
                doctorModifiedNutrients: mealPlan.doctor_modified_nutrients ?? null,
              }
            : null,
          targets,
        };
      }).filter((plan: PatientMealPlan | null) => plan !== null) as PatientMealPlan[];
    } catch (error: any) {
      if (error?.response?.status === 404) {
        console.log('getAllPatientMealPlans: 404 - no meal plans found');
        return [];
      }
      console.error('Failed to get all patient meal plans:', error);
      // Re-throw the error so the caller can handle it
      throw error;
    }
  }

  /**
   * Get a specific meal plan by goal ID
   * Note: This uses the active meal plan endpoint. For non-active goals, we'd need a backend endpoint.
   * For now, we'll get the active one and check if it matches the requested goalId.
   */
  async getPatientMealPlanByGoalId(patientId: number, goalId: number): Promise<PatientMealPlan | null> {
    try {
      // First try to get all meal plans and find the one matching goalId
      const allPlans = await this.getAllPatientMealPlans(patientId);
      const matchingPlan = allPlans.find(plan => plan.goalId === goalId);
      
      if (matchingPlan) {
        console.log(`getPatientMealPlanByGoalId: Found plan with goalId ${goalId} in all plans`);
        return matchingPlan;
      }
      
      // Fallback: Use the doctor endpoint to get meal plan (this gets the active one)
      // This is for backwards compatibility
      console.log(`getPatientMealPlanByGoalId: Goal ${goalId} not found in all plans, trying active plan endpoint`);
      const response = await apiClient.get(
        `/api/v1/doctors/patients/${patientId}/meal-plan`,
      );
      const data = response.data;
      
      console.log('getPatientMealPlanByGoalId response:', { 
        status: response.status, 
        hasData: !!data,
        hasGoal: !!data?.goal,
        goalId: data?.goal?.id,
        requestedGoalId: goalId
      });
      
      if (!data || !data.goal) {
        console.warn('getPatientMealPlanByGoalId: No goal in response');
        return null;
      }

      const goal = data.goal;
      
      // Check if the requested goalId matches the active goal
      if (goal.id !== goalId) {
        // For now, if it's not the active goal, we can't fetch it via doctor endpoint
        // This would require a backend endpoint like /api/v1/doctors/patients/{patient_id}/meal-plan/{goal_id}
        console.warn(`Requested goal ${goalId} is not the active goal. Active goal is ${goal.id}`);
        return null;
      }

      const mealPlan = data.meal_plan || null;
      const targets = Array.isArray(data?.targets) ? data.targets : [];

      return {
        goalId: goal.id,
        goalName: goal.goal_name,
        effectiveAt: goal.effective_at ?? null,
        expiresAt: goal.expires_at ?? null,
        isActive: true,
        targetsContext: mealPlan?.targets_context ?? null,
        mealPlan: mealPlan
          ? {
              breakfast: mealPlan.breakfast,
              lunch: mealPlan.lunch,
              dinner: mealPlan.dinner,
              snacks: mealPlan.snacks,
              totalCaloriesKcal:
                mealPlan.total_calories_kcal ?? mealPlan.totalCaloriesKcal ?? null,
              nutritionProfileId: mealPlan.nutrition_profile_id ?? null,
              baseIcmrRecommendations: mealPlan.base_icmr_recommendations ?? null,
              foodGroupRecommendations: mealPlan.food_group_recommendations ?? null,
              doctorModifiedRecommendations: mealPlan.doctor_modified_recommendations ?? null,
              baseIcmrNutrients: mealPlan.base_icmr_nutrients ?? null,
              adjustedNutrients: mealPlan.adjusted_nutrients ?? null,
              doctorModifiedNutrients: mealPlan.doctor_modified_nutrients ?? null,
            }
          : null,
        targets,
      };
    } catch (error: any) {
      if (error?.response?.status === 404) {
        console.log('getPatientMealPlanByGoalId: 404 - meal plan not found');
        return null;
      }
      console.error('Failed to get patient meal plan by goal ID:', error);
      throw error;
    }
  }

  /**
   * Get the current nutrition goal + meal plan for a patient (doctor view)
   */
  async getPatientMealPlan(patientId: number): Promise<PatientMealPlan | null> {
    try {
      const response = await apiClient.get(
        `/api/v1/doctors/patients/${patientId}/meal-plan`,
      );
      const data = response.data;
      if (!data || !data.goal) {
        return null;
      }

      const goal = data.goal;
      const mealPlan = data.meal_plan || null;
      const targets = Array.isArray(data?.targets) ? data.targets : [];

      const normalized: PatientMealPlan = {
        goalId: goal.id,
        goalName: goal.goal_name,
        effectiveAt: goal.effective_at ?? null,
        expiresAt: goal.expires_at ?? null,
        isActive: true, // This is the active meal plan
        targetsContext: mealPlan?.targets_context ?? null,
        mealPlan: mealPlan
          ? {
              breakfast: mealPlan.breakfast,
              lunch: mealPlan.lunch,
              dinner: mealPlan.dinner,
              snacks: mealPlan.snacks,
              totalCaloriesKcal:
                mealPlan.total_calories_kcal ?? mealPlan.totalCaloriesKcal ?? null,
            }
          : null,
        targets,
      };

      return normalized;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        // No active goal/meal plan yet
        return null;
      }
      console.error('Failed to get patient meal plan:', error);
      throw error;
    }
  }

  /**
   * Create a new adaptive meal plan for a patient (doctor-initiated)
   * Returns response with session_id if waiting for approval
   */
  async createPatientMealPlan(
    patientId: number,
    objective?: string,
    notes?: string,
    preferences?: MealPlanPreferences,
  ): Promise<{ success: boolean; session_id?: number; status?: string; goal_id?: number; message?: string }> {
    try {
      const response = await apiClient.post(`/api/v1/doctors/patients/${patientId}/meal-plan/create`, {
        objective,
        notes,
        preferences: preferences
          ? {
              primary_goal: preferences.primaryGoal,
              secondary_goals: preferences.secondaryGoals ?? [],
              dietary_pattern: preferences.dietaryPattern,
              cuisine_preferences: preferences.cuisinePreferences ?? [],
              disliked_foods: preferences.dislikedFoods ?? [],
              allergies: preferences.allergies ?? [],
              medical_conditions: preferences.medicalConditions ?? [],
              meals_per_day: preferences.mealsPerDay,
              cooking_time_per_meal_min: preferences.cookingTimePerMealMin,
              budget_level: preferences.budgetLevel,
              activity_level: preferences.activityLevel,
              additional_notes: preferences.additionalNotes,
            }
          : undefined,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create patient meal plan:', error);
      throw error;
    }
  }

  /**
   * Delete a diet plan for a patient. Only the doctor who created the plan can delete it.
   */
  async deletePatientMealPlan(patientId: number, goalId: number): Promise<{ success: boolean }> {
    try {
      const response = await apiClient.delete(
        `/api/v1/doctors/patients/${patientId}/meal-plans/${goalId}`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to delete patient meal plan:', error);
      throw error;
    }
  }

  /**
   * Send message to requirements chat session
   * Backend expects: message: str = Body(..., embed=True)
   * FastAPI Body(embed=True) requires the JSON body to have a key matching the parameter name
   * So we need to send: {"message": "text"}
   */
  async sendRequirementsChatMessage(sessionId: number, messageText: string): Promise<any> {
    try {
      const response = await apiClient.post(
        `/api/v1/doctors/requirements-chat/${sessionId}/message`,
        { message: messageText }  // Body(embed=True) expects {"message": "text"}
      );
      return response.data;
    } catch (error) {
      console.error('Failed to send requirements chat message:', error);
      throw error;
    }
  }

  /**
   * Cancel/close an in-progress requirements workflow session (server-side cleanup).
   */
  async cancelRequirementsChatSession(sessionId: number): Promise<any> {
    try {
      const response = await apiClient.delete(`/api/v1/doctors/requirements-chat/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to cancel requirements chat session:', error);
      throw error;
    }
  }

  /**
   * Get chat messages from a session
   */
  async getChatMessages(sessionId: number): Promise<any[]> {
    try {
      const response = await apiClient.get(`/api/v1/chat-sessions/${sessionId}/messages`);
      // API returns List[ChatMessage] directly, not wrapped in a messages property
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Failed to get chat messages:', error);
      throw error;
    }
  }

  /**
   * Long-poll for new chat messages and status updates
   * Returns immediately if new messages exist; otherwise waits up to timeout seconds
   */
  async syncMessages(
    sessionId: number,
    since: number = 0,
    timeout: number = 25
  ): Promise<{
    session_id: number;
    messages: any[];
    status_updates: any[];
    next_since: number;
  }> {
    const sinceParam = Number.isFinite(since) && since >= 0 ? since : 0;
    try {
      const response = await apiClient.get(
        `/api/v1/chat-sessions/${sessionId}/sync?since=${sinceParam}&timeout=${timeout}`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to sync messages:', error);
      throw error;
    }
  }

  /**
   * Get current workflow state including base ICMR and adjusted recommendations
   */
  async getRequirementsChatState(sessionId: number): Promise<any> {
    try {
      const response = await apiClient.get(`/api/v1/doctors/requirements-chat/${sessionId}/state`);
      return response.data;
    } catch (error) {
      console.error('Failed to get requirements chat state:', error);
      throw error;
    }
  }
}

export const patientsService = new PatientsService();
